import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SEO, { breadcrumbSchema, collectionPageSchema, faqSchema } from '@/components/SEO';
import { ArrowRight, CheckCircle2, Star, Users, Calendar, Award, Quote, Phone, Heart, Sparkles, ShieldCheck, MessageCircle } from 'lucide-react';
import { services, serviceCategories } from '@/data/services';
import CTASection from '@/components/CTASection';
import ServiceStickyBar from '@/components/ServiceStickyBar';
import ServiceFinder from '@/components/ServiceFinder';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

const etapes = [
  { phase: '1', title: 'Échange & brief', duree: 'Premier contact', desc: 'Un rendez-vous gratuit et sans engagement pour comprendre votre vision, vos envies, votre budget et vos contraintes. En présentiel à Troyes ou en visio, nous prenons le temps de tout écouter.', detail: 'Nous analysons ensemble vos objectifs, le profil de vos invités, le type d\'ambiance souhaitée et vos inspirations. Ce premier échange est essentiel pour poser les bases d\'un événement qui vous ressemble.' },
  { phase: '2', title: 'Conception créative', duree: 'Proposition', desc: 'Nous imaginons un concept sur-mesure et vous présentons un devis détaillé avec toutes les options, un mood board et un planning prévisionnel.', detail: 'Mood board visuel, proposition de lieux, sélection de prestataires, scénographie, plan budgétaire détaillé… Vous recevez une proposition complète et chiffrée sous 48 à 72h, sans engagement.' },
  { phase: '3', title: 'Organisation & logistique', duree: 'Préparation', desc: 'Sélection et coordination des prestataires, repérages terrain, planification minute par minute. Chaque détail est anticipé et maîtrisé.', detail: 'Visites de lieux, dégustations traiteur, essais musicaux, validation des plans de table, briefing des équipes, planning technique détaillé. Rien n\'est laissé au hasard.' },
  { phase: '4', title: 'Jour J & magie', duree: 'Réalisation', desc: 'Le Ninja entre en action. Coordination en temps réel, gestion des imprévus, supervision totale. Vous profitez pleinement, on s\'occupe de tout.', detail: 'Présent de l\'installation au démontage, Cyrille coordonne chaque prestataire, gère les timings et résout les imprévus avec discrétion et efficacité. Votre seul rôle : profiter.' },
];

const engagements = [
  { title: 'Devis gratuit et transparent', desc: 'Un devis détaillé sous 48 à 72h, sans engagement et sans mauvaise surprise. Chaque ligne est expliquée et justifiée.' },
  { title: 'Interlocuteur unique', desc: 'Cyrille, votre chef de projet dédié du premier appel au lendemain de l\'événement. Un seul numéro, un seul contact.' },
  { title: 'Réseau premium', desc: 'Plus de 100 prestataires et artistes sélectionnés avec exigence sur des critères de qualité, fiabilité et professionnalisme.' },
  { title: 'Budget maîtrisé', desc: 'Gestion budgétaire rigoureuse et transparente. Pas de dépassement, pas de coûts cachés. Votre budget est notre cadre.' },
  { title: 'Présence jour J', desc: 'Coordination complète et présence de l\'installation au démontage. Le Ninja veille à chaque détail pour un événement parfait.' },
  { title: 'Satisfaction vérifiée', desc: '4,9/5 sur Google avec 32 avis clients vérifiés. La meilleure preuve de notre engagement envers l\'excellence.' },
];

const stats = [
  { value: '250+', label: 'Événements depuis avril 2021', icon: Calendar },
  { value: '25 ans', label: 'D\'expérience', icon: Award },
  { value: '100+', label: 'Artistes & prestataires', icon: Users },
  { value: '4,9/5', label: 'Note Google (32 avis)', icon: Star },
];

const faqGlobal = [
  { q: 'Quels types d\'événements organisez-vous ?', a: 'Nous organisons tous types d\'événements : mariages, anniversaires, galas, séminaires d\'entreprise, festivals, inaugurations, soirées privées, team buildings, cérémonies laïques, concerts, spectacles… Du plus intimiste (10 personnes) au plus grand (5 000 participants), nous adaptons notre offre à chaque projet.' },
  { q: 'Dans quelle zone géographique intervenez-vous ?', a: 'Basés à Troyes (Aube), nous intervenons principalement dans le Grand Est : Champagne-Ardenne, Île-de-France, Bourgogne, Picardie. Mais nous nous déplaçons aussi partout en France, et même à l\'étranger sur demande. Aucune limite géographique pour les projets d\'exception.' },
  { q: 'Comment obtenir un devis ?', a: 'Contactez-nous par téléphone, email ou via le formulaire de contact du site. Nous vous répondons sous 48 à 72h et organisons un premier échange gratuit et sans engagement. Après ce rendez-vous, vous recevez un devis détaillé et personnalisé sous 48 à 72h.' },
  { q: 'Peut-on faire appel à Cygnature pour une seule prestation ?', a: 'Oui, chaque prestation est disponible individuellement : uniquement un DJ, uniquement un magicien, uniquement la coordination jour J, uniquement la location de matériel… Vous composez votre événement à la carte selon vos besoins et votre budget.' },
  { q: 'Quel budget prévoir pour un événement ?', a: 'Le budget dépend de nombreux facteurs : type d\'événement, nombre d\'invités, lieu, prestations souhaitées… Nous travaillons avec tous les budgets et vous proposons des solutions adaptées. L\'important pour nous : un résultat qui dépasse vos attentes, quel que soit le budget.' },
  { q: 'Pourquoi choisir Cygnature plutôt qu\'une autre agence ?', a: '25 ans d\'expérience, 250+ événements réussis, un interlocuteur unique (Cyrille) de A à Z, un réseau de 100+ prestataires sélectionnés, une note de 4,9/5 sur Google. Mais surtout : une passion sincère pour chaque projet et une philosophie "Ninja" (discret, efficace, réactif) qui fait la différence le jour J.' },
];

const CATALOG_VISIBLE_SLUGS = [
  'evenements-publics-prives',
  'booking-artistes-spectacles',
  'wedding-planner-designer',
  'officiant-ceremonies-laiques',
  'animations-enfants-famille',
  'lieux-services-associes',
  'animations-speciales-experiences',
  'a-la-location',
];

const Services = () => {
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  return (
    <>
      <SEO
        title="Nos services — Cygnature Production | Agence événementielle Troyes"
        description="Découvrez les 9 pôles d'expertise de Cygnature Production : événements, wedding planning, cérémonies laïques, production artistique, animations, musique et location de matériel. Devis gratuit à Troyes."
        path="/services"
        keywords={['services événementiels','wedding planner','cérémonie laïque','animation','DJ','spectacles','location matériel']}
        jsonLd={[
          breadcrumbSchema([{ name: 'Accueil', path: '/' }, { name: 'Services', path: '/services' }]),
          collectionPageSchema({ name: 'Nos services', description: 'Catalogue complet des services Cygnature Production', path: '/services', itemCount: services.length }),
          faqSchema(faqGlobal.map(f => ({ question: f.q, answer: f.a }))),
        ]}
      />


      {/* Hero + Stats overlay */}
      <section className="bg-petrol text-petrol-foreground">
        <div className="px-6 lg:px-8 pt-20 md:pt-28 pb-28 md:pb-36">
          <div className="max-w-7xl mx-auto">
            <span className="text-xs font-heading font-medium text-primary uppercase tracking-[0.2em]">Nos expertises</span>
            <h1 className="font-heading text-3xl md:text-5xl font-bold mt-4 leading-tight">Tous nos services événementiels</h1>
            <p className="font-heading text-lg md:text-xl text-primary mt-2">Cygnature Production</p>
            <p className="mt-5 text-petrol-foreground/70 max-w-2xl text-sm md:text-base leading-relaxed">
              Mariages, galas, séminaires, festivals, cérémonies laïques, DJ et bien plus encore. Des prestations réalisées par un <strong className="text-petrol-foreground">expert de l'événementiel fort de 25 ans d'expérience</strong>, avec un réseau de prestataires sélectionnés, dans une démarche de qualité totale.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/contact" onClick={() => window.scrollTo(0, 0)} className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-full px-7 py-3 font-heading font-medium text-sm hover:bg-primary/90 transition-colors">
                Demander un devis gratuit <ArrowRight size={14} />
              </Link>
              <a href="tel:+33600000000" className="inline-flex items-center gap-2 rounded-full px-7 py-3 border border-petrol-foreground/20 text-petrol-foreground font-heading font-medium text-sm hover:border-primary hover:text-primary transition-colors">
                <Phone size={14} /> Appeler directement
              </a>
            </div>
          </div>
        </div>
        {/* Stats cards overlapping */}
        <div className="px-6 lg:px-8 -mb-14 relative z-10">
          <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {stats.map((s) => (
              <div key={s.label} className="bg-card rounded-2xl p-5 md:p-6 shadow-lg border border-border/50 flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <s.icon size={18} className="text-primary" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="font-heading text-base md:text-lg font-bold text-foreground leading-tight">{s.value}</p>
                  <p className="text-[10px] md:text-xs text-muted-foreground mt-0.5">{s.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Spacer for overlapping cards */}
      <div className="h-20 bg-background" />

      {/* Intro text */}
      <section className="px-6 lg:px-8 py-12 md:py-16 bg-background">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground mb-4">Une agence événementielle à taille humaine, des ambitions XXL</h2>
          <p className="text-sm md:text-base text-muted-foreground leading-relaxed mb-4">
            Cygnature Production n'est pas une agence comme les autres. Fondée et dirigée par Cyrille Lecourt, un professionnel de l'événementiel fort de 25 années d'expérience et de plus de 250 événements orchestrés, notre agence place l'humain au cœur de chaque projet. Pas de commercial intermédiaire, pas de standardisation : c'est Cyrille lui-même qui vous accompagne du premier appel au lendemain de votre événement.
          </p>
          <p className="text-sm md:text-base text-muted-foreground leading-relaxed mb-4">
            Notre philosophie ? Être des <strong className="text-foreground">Ninjas de l'événementiel</strong>. Discrets mais omniprésents, réactifs et polyvalents, nous anticipons chaque besoin, gérons chaque imprévu avec une efficacité redoutable et veillons à ce que chaque instant soit parfait — sans jamais voler la vedette. Le jour J, vous ne nous voyez pas, mais tout fonctionne comme par magie.
          </p>
          <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
            Basés à <strong className="text-foreground">Troyes, dans l'Aube</strong>, nous intervenons dans tout le <strong className="text-foreground">Grand Est</strong> (Champagne-Ardenne, Île-de-France, Bourgogne) et partout en France. Notre réseau de plus de 100 prestataires sélectionnés nous permet de proposer un service complet et qualitatif, quelle que soit la localisation de votre événement.
          </p>
        </div>
      </section>

      {/* Services grid with filter */}
      <section className="px-6 lg:px-8 py-16 md:py-20 bg-card">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs font-heading font-semibold uppercase tracking-[0.15em] text-primary mb-2">Catalogue</p>
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground mb-3">Toutes nos prestations</h2>
          <p className="text-sm text-muted-foreground max-w-2xl mb-8">
            Chaque prestation est personnalisable et adaptable à votre projet. Cliquez sur un service pour découvrir le détail de notre offre, les points clés, les FAQ et un témoignage client.
          </p>

          {/* Filter tabs */}
          <div className="flex flex-wrap gap-2 mb-8">
            <button
              onClick={() => setActiveCategory(null)}
              className={`px-4 py-2 rounded-full text-xs font-heading font-medium transition-all ${
                activeCategory === null
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-background border border-border text-muted-foreground hover:border-primary/30 hover:text-foreground'
              }`}
            >
              Tout voir
            </button>
            {serviceCategories.map((cat) => (
              <button
                key={cat.key}
                onClick={() => setActiveCategory(cat.key)}
                className={`px-4 py-2 rounded-full text-xs font-heading font-medium transition-all ${
                  activeCategory === cat.key
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-background border border-border text-muted-foreground hover:border-primary/30 hover:text-foreground'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {/* Flat grid */}
          <div className="flex flex-wrap justify-center gap-5">
            {services
              .filter((s) => CATALOG_VISIBLE_SLUGS.includes(s.slug))
              .filter((s) => !activeCategory || s.category === activeCategory)
              .map((s) => (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  onClick={() => window.scrollTo(0, 0)}
                  className="group w-full max-w-sm md:max-w-[calc(50%-0.625rem)] lg:max-w-[22rem] lg:flex-[0_1_22rem] p-6 rounded-xl border border-border bg-background hover:border-primary/30 hover:shadow-md transition-all"
                >
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                    <s.icon size={22} className="text-primary" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-heading font-bold text-base text-foreground">{s.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed line-clamp-3">{s.shortDesc}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-medium text-primary group-hover:gap-2.5 transition-all">
                    Découvrir cette prestation <ArrowRight size={13} />
                  </span>
                </Link>
              ))}
          </div>
        </div>
      </section>

      {/* Service Finder Questionnaire */}
      <ServiceFinder />

      {/* CTA après questionnaire */}
      <section className="px-6 lg:px-8 pt-0 pb-10 md:pb-12 bg-background">
        <div className="max-w-2xl mx-auto text-center">
          <p className="text-sm text-muted-foreground mb-4">Vous préférez en parler directement avec nous&#8239;?</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/contact" onClick={() => window.scrollTo(0, 0)} className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-full px-6 py-2.5 font-heading font-medium text-sm hover:bg-primary/90 transition-colors">
              <Calendar size={15} /> Prendre rendez-vous
            </Link>
            <a href="tel:+33325494243" className="inline-flex items-center gap-2 border border-border text-foreground rounded-full px-6 py-2.5 font-heading font-medium text-sm hover:border-primary/30 hover:text-primary transition-colors">
              <Phone size={15} /> Nous appeler
            </a>
          </div>
        </div>
      </section>

      {/* Étapes */}
      <section className="px-6 lg:px-8 py-16 md:py-20 bg-background">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs font-heading font-semibold uppercase tracking-[0.15em] text-primary mb-2">Méthodologie</p>
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground">Notre accompagnement en 4 étapes</h2>
          <p className="mt-3 text-sm text-muted-foreground max-w-2xl">
            Un processus éprouvé sur plus de 250 événements pour garantir la réussite du vôtre. À chaque étape, un seul interlocuteur : Cyrille.
          </p>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {etapes.map((s) => (
              <div key={s.title} className="p-6 rounded-xl border border-border bg-card">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                  <span className="font-heading font-black text-primary text-lg">{s.phase}</span>
                </div>
                <span className="text-xs font-heading font-semibold text-primary uppercase tracking-wider">Étape {s.phase} — {s.duree}</span>
                <h3 className="font-heading font-bold text-sm text-foreground mt-1">{s.title}</h3>
                <p className="mt-2 text-xs text-muted-foreground leading-relaxed">{s.desc}</p>
                <p className="mt-2 text-xs text-muted-foreground/70 leading-relaxed italic">{s.detail}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why choose us */}
      <section className="px-6 lg:px-8 py-16 md:py-20 bg-card">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs font-heading font-semibold uppercase tracking-[0.15em] text-primary mb-2">Pourquoi nous</p>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground">Ce qui fait la différence de Cygnature</h2>
            <p className="mt-3 text-sm text-muted-foreground max-w-xl mx-auto">Au-delà des prestations, c'est une philosophie, une exigence et une passion qui nous animent au quotidien.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: ShieldCheck, title: 'Expertise de 25 ans', desc: 'Plus de 250 événements réussis dans tous les formats. Une expérience irremplaçable qui se traduit par une anticipation parfaite de chaque détail et de chaque imprévu.' },
              { icon: Users, title: 'Interlocuteur unique', desc: 'Cyrille est votre chef de projet dédié du premier appel au jour J. Pas de commercial, pas de sous-traitance masquée. Une relation humaine et directe.' },
              { icon: Heart, title: 'Passion sincère', desc: '« Je prends place à côté de mes clients, j\'agis comme si c\'est pour moi. » Chaque événement est vécu comme un projet personnel, avec la même passion et la même exigence.' },
              { icon: Sparkles, title: 'Créativité sans limite', desc: 'Chaque événement est une page blanche. Pas de copier-coller, pas de template. Nous créons des expériences uniques qui racontent votre histoire.' },
              { icon: MessageCircle, title: 'Écoute & réactivité', desc: 'Disponible 7j/7, Cyrille répond à vos questions sous 2h. Une réactivité de Ninja qui rassure et qui simplifie chaque étape de l\'organisation.' },
              { icon: Star, title: '4,9/5 sur Google', desc: '32 avis clients vérifiés avec une note quasi-parfaite. La meilleure recommandation possible : celle de nos clients satisfaits.' },
            ].map((item) => (
              <div key={item.title} className="p-5 rounded-xl border border-border bg-background">
                <item.icon size={20} className="text-primary mb-3" strokeWidth={1.5} />
                <h3 className="font-heading font-bold text-sm text-foreground">{item.title}</h3>
                <p className="text-xs text-muted-foreground mt-2 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial quote */}
      <section className="px-6 lg:px-8 py-16 md:py-20 bg-background">
        <div className="max-w-3xl mx-auto text-center">
          <Quote size={32} className="text-primary/30 mx-auto mb-4" />
          <blockquote className="font-heading text-base md:text-lg text-foreground leading-relaxed italic">
            « Cyrille est un magicien de l'événementiel. Son écoute, sa créativité et son professionnalisme ont transformé notre mariage en un rêve éveillé. On recommande les yeux fermés. »
          </blockquote>
          <p className="mt-4 text-sm text-muted-foreground">— <strong className="text-foreground">Marie & Thomas</strong>, Mariage — Domaine dans l'Aube</p>
          <div className="flex justify-center gap-0.5 mt-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <Star key={i} size={14} className="fill-amber-400 text-amber-400" />
            ))}
          </div>
        </div>
      </section>

      {/* Engagements */}
      <section className="px-6 lg:px-8 py-16 md:py-20 bg-petrol text-petrol-foreground">
        <div className="max-w-5xl mx-auto">
          <p className="text-xs font-heading font-semibold uppercase tracking-[0.15em] text-primary mb-2">Garanties</p>
          <h2 className="font-heading text-xl md:text-2xl font-bold">Nos engagements qualité</h2>
          <p className="mt-3 text-sm text-petrol-foreground/60 max-w-2xl leading-relaxed">
            Chez Cygnature, la qualité n'est pas une option. C'est un engagement quotidien envers chacun de nos clients. Voici les garanties que nous prenons pour chaque événement.
          </p>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {engagements.map((e) => (
              <div key={e.title} className="flex items-start gap-3 p-5 rounded-xl border border-petrol-foreground/10 bg-petrol-foreground/5">
                <CheckCircle2 size={18} className="text-primary shrink-0 mt-0.5" strokeWidth={1.5} />
                <div>
                  <h3 className="text-sm text-petrol-foreground font-heading font-bold">{e.title}</h3>
                  <p className="text-xs text-petrol-foreground/60 mt-1 leading-relaxed">{e.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="px-6 lg:px-8 py-16 md:py-20 bg-background">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs font-heading font-semibold uppercase tracking-[0.15em] text-primary mb-2">Questions fréquentes</p>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground">Tout savoir sur nos services</h2>
            <p className="mt-3 text-sm text-muted-foreground">Les réponses aux questions les plus posées par nos clients.</p>
          </div>
          <Accordion type="single" collapsible className="space-y-2">
            {faqGlobal.map((item, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border border-border rounded-xl px-5 bg-card">
                <AccordionTrigger className="text-sm font-heading font-medium text-foreground hover:text-primary text-left py-4">{item.q}</AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground leading-relaxed pb-4">{item.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* SEO block */}
      <section className="px-6 lg:px-8 py-12 md:py-16 bg-card">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading text-lg font-bold text-foreground mb-4">Votre agence événementielle à Troyes, dans l'Aube et le Grand Est</h2>
          <div className="text-sm text-muted-foreground leading-relaxed space-y-3">
            <p>
              Cygnature Production est l'agence événementielle de référence à Troyes pour l'organisation de vos événements privés et professionnels. Fondée par Cyrille Lecourt, un expert de l'événementiel avec plus de 25 ans d'expérience et plus de 250 événements réussis à son actif, notre agence propose 9 pôles d'expertise complémentaires : organisation d'événements publics et privés, production de talents artistiques, wedding planning et wedding design, officiant de cérémonies laïques, animations enfants et famille, musique et ambiance (DJ, musiciens live, groupes), lieux et services associés, animations spéciales et expériences immersives, et location de matériel événementiel.
            </p>
            <p>
              Basés à Troyes dans l'Aube, nous intervenons dans tout le Grand Est (Reims, Châlons-en-Champagne, Épernay, Sens, Auxerre, Chaumont, Bar-sur-Aube, Nogent-sur-Seine, Romilly-sur-Seine, Saint-Dizier) ainsi qu'en Île-de-France et partout en France. Notre réseau de plus de 100 prestataires et artistes sélectionnés avec exigence nous permet de proposer un service complet et qualitatif pour tous vos projets.
            </p>
            <p>
              Que vous planifiiez un mariage, un anniversaire, un gala, un séminaire d'entreprise, un festival ou tout autre événement, Cygnature Production vous accompagne avec passion, créativité et professionnalisme. Contactez-nous dès aujourd'hui pour un premier échange gratuit et un devis personnalisé sous 48 à 72h.
            </p>
          </div>
        </div>
      </section>

      <CTASection
        title="Un projet d'événement ?"
        subtitle="Contactez-nous pour un premier échange gratuit et sans engagement. Nous créerons ensemble l'événement de vos rêves. Devis personnalisé sous 48 à 72h."
        buttonLabel="Demander un devis gratuit"
      />
      <ServiceStickyBar />
    </>
  );
};

export default Services;
